import React, { useEffect, useState } from "react";
import { BarChart3, Rocket, Users, Star, Percent, Recycle, Zap } from "lucide-react";
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import Loading from "../components/Loading";

const AXIS = { stroke: "#67e8f9", fontSize: 11, fontFamily: "JetBrains Mono, monospace" };
const TOOLTIP_STYLE = {
  background: "#000",
  border: "1px solid #22d3ee",
  color: "#e6f7ff",
  fontFamily: "JetBrains Mono, monospace",
  fontSize: "11px",
  padding: "6px 10px",
};

const PIE_COLORS = ["#22d3ee", "#67e8f9", "#a5f3fc", "#f472b6", "#fbbf24", "#a78bfa", "#f87171", "#4ade80"];

function StatCell({ icon: Icon, label, value, testid }) {
  return (
    <div className="sat-brackets sat-surface border border-[color:var(--sat-border-bright)] p-4" data-testid={testid}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={12} className="sat-cyan" />
        <div className="sat-label sat-cyan">{label}</div>
      </div>
      <div className="font-display font-bold text-3xl">{value}</div>
    </div>
  );
}

export default function StatsPage() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    api.get("/stats")
      .then(r => alive && setData(r.data))
      .catch(() => alive && setData(null))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [user]); // refetch when auth state changes to include favorites

  if (loading) return <Loading message="AGGREGATING TELEMETRY" />;
  if (!data) return <div className="sat-label text-center py-12" data-testid="stats-empty">TELEMETRY UNAVAILABLE</div>;

  const fleet = data.fleet_totals || {};
  const yearRows = data.launches_by_year || [];
  const rateRows = data.success_rate_by_year || [];
  const agencies = data.top_agencies || [];
  const favs = data.favorites_breakdown;

  return (
    <div data-testid="page-stats" className="space-y-8">
      <div className="border-b border-[color:var(--sat-border)] pb-4">
        <div className="sat-label sat-cyan flex items-center gap-2"><BarChart3 size={12} /> DIRECTIVE // STATS</div>
        <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">TELEMETRY DASHBOARD</h1>
        <p className="text-sm text-[color:var(--sat-text-dim)] mt-2">
          Aggregate insight across launches, providers, fleet totals{user ? " and your archive" : ""}.
        </p>
      </div>

      {/* Fleet totals cards */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3" data-testid="stats-fleet-cards">
        <StatCell icon={Rocket} label="ROCKETS TRACKED" value={fleet.total ?? "—"} testid="stat-fleet-total" />
        <StatCell icon={Zap} label="ACTIVE" value={fleet.active ?? "—"} testid="stat-fleet-active" />
        <StatCell icon={Recycle} label="REUSABLE" value={fleet.reusable ?? "—"} testid="stat-fleet-reusable" />
      </div>

      {/* Launches by year */}
      <section className="sat-brackets sat-surface p-4 border border-[color:var(--sat-border-bright)]" data-testid="stats-launches-by-year">
        <div className="flex items-center gap-2 mb-3">
          <Rocket size={12} className="sat-cyan" />
          <div className="sat-label sat-cyan">LAUNCHES PER YEAR</div>
        </div>
        <div style={{ width: "100%", height: 260 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={yearRows}>
              <CartesianGrid stroke="#164e63" strokeDasharray="2 4" />
              <XAxis dataKey="year" {...AXIS} />
              <YAxis {...AXIS} />
              <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(34,211,238,0.08)" }} />
              <Bar dataKey="count" fill="#22d3ee" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Success rate over time */}
      <section className="sat-brackets sat-surface p-4 border border-[color:var(--sat-border-bright)]" data-testid="stats-success-rate">
        <div className="flex items-center gap-2 mb-3">
          <Percent size={12} className="sat-cyan" />
          <div className="sat-label sat-cyan">SUCCESS RATE (%)</div>
        </div>
        <div style={{ width: "100%", height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={rateRows}>
              <CartesianGrid stroke="#164e63" strokeDasharray="2 4" />
              <XAxis dataKey="year" {...AXIS} />
              <YAxis domain={[0, 100]} {...AXIS} />
              <Tooltip contentStyle={TOOLTIP_STYLE} />
              <Line type="monotone" dataKey="rate" stroke="#22d3ee" strokeWidth={2} dot={{ fill: "#22d3ee", r: 3 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </section>

      {/* Agencies + favorites (side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <section className="sat-brackets sat-surface p-4 border border-[color:var(--sat-border-bright)]" data-testid="stats-agencies">
          <div className="flex items-center gap-2 mb-3">
            <Users size={12} className="sat-cyan" />
            <div className="sat-label sat-cyan">TOP AGENCIES</div>
          </div>
          {agencies.length === 0 ? (
            <div className="sat-label text-[color:var(--sat-text-dim)]">NO DATA</div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={agencies} layout="vertical" margin={{ left: 10 }}>
                  <CartesianGrid stroke="#164e63" strokeDasharray="2 4" />
                  <XAxis type="number" {...AXIS} />
                  <YAxis dataKey="agency" type="category" width={140} {...AXIS} interval={0} />
                  <Tooltip contentStyle={TOOLTIP_STYLE} cursor={{ fill: "rgba(34,211,238,0.08)" }} />
                  <Bar dataKey="count" fill="#67e8f9" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>

        <section className="sat-brackets sat-surface p-4 border border-[color:var(--sat-border-bright)]" data-testid="stats-favorites">
          <div className="flex items-center gap-2 mb-3">
            <Star size={12} className="sat-cyan" />
            <div className="sat-label sat-cyan">YOUR ARCHIVE</div>
          </div>
          {!user ? (
            <div className="sat-label text-[color:var(--sat-text-dim)]" data-testid="stats-favorites-guest">
              LOG IN TO SEE YOUR FAVORITES BREAKDOWN
            </div>
          ) : !favs || favs.length === 0 ? (
            <div className="sat-label text-[color:var(--sat-text-dim)]">NO FAVORITES YET</div>
          ) : (
            <div style={{ width: "100%", height: 260 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={favs} dataKey="count" nameKey="kind" outerRadius={90} label={(e) => `${e.kind} ${e.count}`}>
                    {favs.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                  </Pie>
                  <Tooltip contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
